import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { getTeamByID, getTeamSeasons, getTeamGames } from "../../services/teamsAPI";
import { Modal } from "../../components/modals/Modal";
import { CreateTeam } from "../../components/admin/CreateTeam";
import { BasicTable } from "../../components/tables/BasicTable";
import { Pagination } from "../../components/pagination/Pagination";
import { SeasonCarousel } from "../../components/seasons/SeasonCarousel";
import { formatDate } from "../../utils/dateAndTime";
import Loader from "../../components/ui/loader";
import { useAuthStore } from "../../store/authStore";
import teamPlaceholder from "../../assets/team-logo-placeholder.svg";

export const Team = () => {
  const { teamID } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasImageError, setHasImageError] = useState(false);
  const { user } = useAuthStore();

  // Games state
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasonSearch, setSeasonSearch] = useState("");
  const [gameData, setGameData] = useState([]);
  const [gamesPagination, setGamesPagination] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(false);
  const searchTimerRef = useRef(null);

  const fetchTeam = async () => {
    setIsLoading(true);
    try {
      let response = await getTeamByID(teamID);
      if (response.success) {
        setTeam(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSeasons = useCallback(async (search = "") => {
    try {
      const response = await getTeamSeasons(teamID, search);
      if (response?.success) {
        setSeasons(response.data.seasons);
        return response.data;
      }
    } catch (error) {
      console.log(error);
    }
    return null;
  }, [teamID]);

  const fetchGames = useCallback(async (seasonId, page = 1) => {
    if (!seasonId) return;
    setGamesLoading(true);
    try {
      const query = `?season=${seasonId}&page=${page}&pageSize=50`;
      const response = await getTeamGames(teamID, query);
      if (response?.success) {
        setGameData(response.data.games);
        setGamesPagination(response.data.pagination);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setGamesLoading(false);
    }
  }, [teamID]);

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Initialize: fetch team + seasons
  useEffect(() => {
    fetchTeam();
    fetchSeasons().then((data) => {
      if (data) {
        const urlSeason = searchParams.get("season");
        const initialSeason = urlSeason ? parseInt(urlSeason) : data.defaultSeasonId;
        if (initialSeason) {
          setSelectedSeason(initialSeason);
        }
      }
    });
  }, [teamID]); //eslint-disable-line

  // Fetch games when season or page changes
  useEffect(() => {
    if (selectedSeason) {
      const page = parseInt(searchParams.get("page")) || 1;
      fetchGames(selectedSeason, page);
    }
  }, [selectedSeason, searchParams, fetchGames]);

  useEffect(() => {
    setHasImageError(false);
  }, [teamID, team?.teamImageFile]);

  const handleSeasonChange = (seasonId) => {
    setSelectedSeason(seasonId);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("season", seasonId);
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleSeasonSearch = (value) => {
    setSeasonSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchSeasons(value);
    }, 300);
  };

  // Navigation handlers for games table
  const handleTeamClick = (id) => navigate(`/team/${id}`);
  const handleGameClick = (id) => navigate(`/game/${id}`);
  const handleLeagueClick = (id) => navigate(`/league/${id}`);

  const gamesColumns = [
    {
      key: "date",
      label: "Date",
      format: formatDate,
      itemID: "GameID",
      clickHandler: handleGameClick,
    },
    {
      key: "gameType",
      label: "Game Type",
      itemID: "GameID",
      clickHandler: handleGameClick,
    },
    { key: "round", label: "Round", itemID: "GameID", clickHandler: handleGameClick },
    {
      key: "description",
      label: "Game Description",
      itemID: "GameID",
      clickHandler: handleGameClick,
    },
    {
      key: "LeagueDetails.name",
      label: "League",
      itemID: "LeagueDetails.LeagueID",
      clickHandler: handleLeagueClick,
    },
    {
      key: "SeasonDetails.season",
      label: "Season",
      itemID: "GameID",
      clickHandler: handleGameClick,
    },
    {
      key: "HomeTeamDetails.city",
      itemID: "HomeTeamDetails.TeamID",
      label: "Home Team",
      clickHandler: handleTeamClick,
    },
    {
      key: "AwayTeamDetails.city",
      itemID: "AwayTeamDetails.TeamID",
      label: "Away Team",
      clickHandler: handleTeamClick,
    },
    {
      key: "Fights",
      label: "# of Fights",
      format: (fights) => (fights ? fights.length : 0),
      itemID: "GameID",
      clickHandler: handleGameClick,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-flyers-black">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-flyers-black text-white">
      {team && (
        <div className="max-w-6xl mx-auto">
          {/* Profile Hero Card */}
          <div className="bg-flyers-charcoal border border-flyers-dark-gray rounded-lg p-6">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              {/* Team Logo / Fallback */}
              <div className="flex-shrink-0">
                {team.teamImageFile && !hasImageError ? (
                  <img
                    src={team.teamImageFile}
                    alt={`${team.city} ${team.name} logo`}
                    className="w-48 h-48 md:w-56 md:h-56 object-contain rounded-lg border-2 border-flyers-dark-gray p-2"
                    onError={() => setHasImageError(true)}
                  />
                ) : (
                  <img
                    src={teamPlaceholder}
                    alt="Team logo placeholder"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain bg-flyers-black rounded-lg border-2 border-flyers-dark-gray p-2"
                  />
                )}
              </div>

              {/* Team Details */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-wide text-flyers-orange">
                  {team.city} {team.name}
                </h1>

                <div className="w-16 h-0.5 bg-flyers-orange mt-3 mb-4 mx-auto md:mx-0" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  {team.city && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">City</span>
                      <p className="text-white font-semibold">{team.city}</p>
                    </div>
                  )}
                  {team.name && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">Name</span>
                      <p className="text-white font-semibold">{team.name}</p>
                    </div>
                  )}
                  {team?.LeagueDetails?.name && (
                    <div>
                      <span className="text-flyers-mid-gray text-sm font-heading uppercase tracking-wider">League</span>
                      <p>
                        <Link
                          to={`/league/${team.LeagueDetails.LeagueID}`}
                          className="text-flyers-orange font-semibold hover:text-flyers-orange-light transition-colors"
                        >
                          {team.LeagueDetails.name}
                        </Link>
                      </p>
                    </div>
                  )}
                </div>

                {team.description && (
                  <p className="mt-4 text-flyers-light-gray leading-relaxed">
                    {team.description}
                  </p>
                )}

                {user?.role === "admin" && (
                  <div className="mt-4">
                    <button
                      onClick={() => setOpenModal(true)}
                      className="px-4 py-2 bg-flyers-orange text-white font-heading font-bold uppercase tracking-wider rounded hover:bg-flyers-orange-light transition-colors duration-150"
                    >
                      Update Team
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Games Section */}
          <div className="mt-8 space-y-4">
            <h2 className="font-heading text-2xl font-black uppercase tracking-wide text-flyers-orange">
              Games
            </h2>

            <SeasonCarousel
              seasons={seasons}
              selectedSeason={selectedSeason}
              onSeasonChange={handleSeasonChange}
              onSearch={handleSeasonSearch}
              searchValue={seasonSearch}
            />

            {gamesLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader />
              </div>
            ) : (
              <>
                <BasicTable columns={gamesColumns} data={gameData} />
                <Pagination pagination={gamesPagination} />
              </>
            )}
          </div>
        </div>
      )}

      {isOpenModal && (
        <Modal isOpen={isOpenModal} close={handleCloseModal}>
          <CreateTeam
            teamID={teamID}
            team={team}
            handleClose={handleCloseModal}
            fetchTeam={fetchTeam}
          />
        </Modal>
      )}
    </div>
  );
};
